import {
  Alert,
  Box,
  Button,
  Group,
  Pagination,
  Select,
  Skeleton,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowsSort,
  IconRefresh,
  IconSortAscending,
  IconSortDescending,
} from "@tabler/icons-react";
import { forwardRef } from "react";
import { useFitViewport } from "@/hooks/useFitViewport";
import { PER_PAGE_OPTIONS } from "@/lib/format";

// Kategoriler/Ürünler sayfalarındaki liste görünümünün ortak parçaları — tüm
// tablolar aynı tipografi ve satır düzenini kullanır.
//
// columns: [{ label, w, ta, flex, spacer, sortKey }] — flex: kalan alanı doldurur,
// spacer: başlıksız sabit boşluk (drag kolu / avatar hizası için), sortKey: kolonu
// tıklanabilir yapar (ListHeader'a sort + onSort verilmişse).

// Dar ekranda (telefon) sabit-genişlikli kolonların taşmasını önler: içeriği
// yatay kaydırılabilir bir kutuya sarar, minWidth altına inmez.
//
// Dikeyde de ekranın alt kenarında bitiyor: liste uzadıkça sayfanın tamamı kayıyordu ve altındaki
// sayfalama şeridi görünmez oluyordu — kullanıcı listeyi kaydırmak için önce sayfayı kaydırmak
// zorundaydı. Artık kayan tek şey satırlar; başlık yerinde duruyor.
//
// fit={false}: modal içindeki listeler. Orada pencere değil modalın kendi gövdesi kaydırılıyor,
// ekran ölçüsüne göre kırpmak modalın dışına taşan bir kutu üretirdi.
export function ListScroll({ minWidth, fit = true, children }) {
  const viewport = useFitViewport({
    rowSelector: "[data-list-row]",
    headerSelector: "[data-list-header]",
  });

  return (
    <Box
      ref={fit ? viewport.ref : undefined}
      style={{
        overflowX: "auto",
        overflowY: fit ? "auto" : undefined,
        maxHeight: fit ? viewport.maxHeight : undefined,
      }}
    >
      <Box miw={minWidth}>{children}</Box>
    </Box>
  );
}

const SORT_ICON = { asc: IconSortAscending, desc: IconSortDescending };

// Sıralanabilir başlık: aktifken yön ikonu, pasifken soluk çift ok. Tıklama sırası
// artan -> azalan -> sırasız, yani kullanıcı varsayılan (sırasız) hale dönebilir.
function SortableLabel({ label, sortKey, sort, onSort, active }) {
  const Icon = active ? SORT_ICON[sort.dir] : IconArrowsSort;

  return (
    <UnstyledButton onClick={() => onSort(sortKey)}>
      <Group gap={4} wrap="nowrap">
        <Text
          size="xs"
          fw={700}
          c={active ? "chocolate" : "dimmed"}
          tt="uppercase"
        >
          {label}
        </Text>
        <Icon size={13} opacity={active ? 1 : 0.4} />
      </Group>
    </UnstyledButton>
  );
}

export function ListHeader({ columns, sort, onSort }) {
  const renderLabel = (column) =>
    column.sortKey && onSort ? (
      <SortableLabel
        label={column.label}
        sortKey={column.sortKey}
        sort={sort}
        onSort={onSort}
        active={sort?.key === column.sortKey}
      />
    ) : (
      <Text size="xs" fw={700} c="dimmed" tt="uppercase">
        {column.label}
      </Text>
    );

  return (
    <Group
      data-list-header
      wrap="nowrap"
      gap="md"
      px="sm"
      py="xs"
      bg="var(--mantine-color-gray-0)"
      style={{
        borderBottom: "1px solid var(--mantine-color-gray-2)",
        // Başlık kaydırma kutusunun içinde duruyor; yapışkan olmasa satırlarla birlikte
        // yukarı kayıp giderdi.
        position: "sticky",
        top: 0,
        zIndex: 2,
      }}
    >
      {columns.map((column, index) =>
        column.spacer ? (
          <Box key={index} w={column.w} />
        ) : (
          // Satır hücreleriyle (ListCell) birebir aynı kutu: aynı w, aynı flex,
          // aynı justifyContent — yoksa başlıklar satırlarla hizalanmıyor.
          <Box
            key={index}
            w={column.flex ? undefined : column.w}
            style={{
              display: "flex",
              justifyContent: taToJustify(
                column.ta ?? (column.flex ? "left" : "center"),
              ),
              ...(column.flex ? { flex: 1, minWidth: 0 } : { flexShrink: 0 }),
            }}
          >
            {renderLabel(column)}
          </Box>
        ),
      )}
    </Group>
  );
}

export const ListRow = forwardRef(function ListRow({ children, ...rest }, ref) {
  return (
    <Group
      ref={ref}
      data-list-row
      wrap="nowrap"
      gap="md"
      px="sm"
      py="xs"
      bd="1px solid var(--mantine-color-gray-1)"
      {...rest}
    >
      {children}
    </Group>
  );
});

export function ListCell({ w, ta, flex, children }) {
  if (flex) {
    return (
      <Box style={{ flex: 1, minWidth: 0 }} ta={ta}>
        {children}
      </Box>
    );
  }

  return (
    <Box
      w={w}
      ta={ta ?? "center"}
      style={{
        display: "flex",
        justifyContent: taToJustify(ta),
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}

const taToJustify = (ta) =>
  ta === "left" ? "flex-start" : ta === "right" ? "flex-end" : "center";

export function ListEmpty({ children }) {
  return (
    <Text c="dimmed" ta="center" py="xl">
      {children}
    </Text>
  );
}

// Bir istek düştüğünde liste boş görünürdü ve "kayıt yok" ile "veri gelemedi" aynı ekrana
// çıkıyordu — kullanıcı silinmiş sanıyordu. Bu yüzden hata AYRI görünür ve bunu açıkça söyler.
export function ListError({ title = "Veriler alınamadı", onRetry, children }) {
  return (
    <Alert
      color="red"
      variant="light"
      icon={<IconAlertTriangle size={18} />}
      title={title}
      my="md"
    >
      <Stack gap="sm" align="flex-start">
        <Text size="sm">
          {children ??
            "Bağlantı kurulamadı — bu boş bir liste değil, veriler yüklenemedi."}
        </Text>
        {onRetry && (
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconRefresh size={14} />}
            onClick={onRetry}
          >
            Tekrar dene
          </Button>
        )}
      </Stack>
    </Alert>
  );
}

// Yükleme sırasında satırların yerini tutar. Ortada dönen bir çember yerine bunu göstermek
// düzeni sabit tutuyor: liste geldiğinde sayfa zıplamıyor ve bekleme daha kısa hissediliyor.
//
// columns ListHeader'a verilen dizinin AYNISI — genişlikler ve hizalar oradan geldiği için
// iskelet gerçek satırla birebir örtüşüyor, kabaca benzeyen bir taklit değil.
export function ListSkeleton({ columns, rows = 6 }) {
  return Array.from({ length: rows }, (_, rowIndex) => (
    <ListRow key={rowIndex}>
      {columns.map((column, index) =>
        column.spacer ? (
          <Box key={index} w={column.w} />
        ) : (
          <ListCell key={index} w={column.w} ta={column.ta} flex={column.flex}>
            <Skeleton
              height={column.flex ? 14 : 12}
              // Sabit genişlikli kolonlarda tam genişlik doldurmak sahte bir yoğunluk
              // yaratıyor; kolon genişliğinin bir kısmı gerçek içeriğe daha yakın.
              width={column.flex ? "55%" : "70%"}
              radius="sm"
            />
          </ListCell>
        ),
      )}
    </ListRow>
  ));
}

// Sayfa altı çubuğu: sayfa başı seçimi, toplam ve sayfalama. Aynı yirmi satır yeni her listede
// yeniden yazılıyordu.
export function ListPagination({
  page,
  onPage,
  perPage,
  onPerPage,
  total,
  unit,
  totalPages,
}) {
  return (
    <Group justify="space-between" p="sm">
      <Group gap="xs">
        <Text size="sm" c="dimmed">
          Sayfa başı
        </Text>
        <Select
          withCheckIcon={false}
          w={75}
          size="xs"
          data={PER_PAGE_OPTIONS}
          value={String(perPage)}
          onChange={(value) => onPerPage(Number(value))}
        />
        <Text size="sm" c="dimmed">
          {total} {unit}
        </Text>
      </Group>
      <Pagination
        withEdges
        total={totalPages}
        value={page}
        onChange={onPage}
        size="sm"
      />
    </Group>
  );
}
